import React, { useEffect, useRef, useId } from "react";
import { createPortal } from "react-dom";

// Stack global para rastrear modais abertos (LIFO - Last In First Out)
const modalStack: string[] = [];

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  title?: string;
  isExpanded?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  className = "",
  title,
  isExpanded = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const modalId = useId();

  // Gerencia o stack de modais
  useEffect(() => {
    if (isOpen) {
      modalStack.push(modalId);
    }
    return () => {
      const index = modalStack.indexOf(modalId);
      if (index > -1) {
        modalStack.splice(index, 1);
      }
    };
  }, [isOpen, modalId]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      // Só processa ESC se este modal for o mais recente (topo do stack)
      if (event.key === "Escape" && modalStack.at(-1) === modalId) {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose, modalId]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    // Só processa o clique se este modal for o mais recente (topo do stack)
    if (modalStack.at(-1) !== modalId) {
      return;
    }
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      event.stopPropagation();
      onClose();
    }
  };

  const getContentClassName = (expanded: boolean) => {
    const baseClass = "overflow-y-auto custom-scrollbar";
    const heightClass = expanded
      ? "h-full"
      : "h-full sm:max-h-[calc(100vh-8rem)]";
    return `${baseClass} ${heightClass}`;
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[999999] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto transition-all duration-300 ${
        isExpanded ? "p-0" : "p-0 sm:p-4"
      }`}
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className={`relative w-full bg-white shadow-2xl dark:bg-boxdark dark:border dark:border-strokedark transition-all duration-300 ${
          isExpanded
            ? "h-screen w-screen max-h-screen rounded-none border-0 p-0"
            : "h-[100dvh] sm:h-auto max-w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl sm:rounded-b-3xl overflow-hidden"
        } ${className}`}
      >
        {/* Botão de fechar */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 sm:right-5 sm:top-5 z-10 p-2.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all active:scale-95 touch-manipulation"
          aria-label="Fechar modal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 sm:h-7 sm:w-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Título */}
        {title && (
          <div className="px-6 pt-6 pb-4 sm:px-8 sm:pt-8 sm:pb-5 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white pr-12">
              {title}
            </h3>
          </div>
        )}

        {/* Conteúdo */}
        <div
          className={
            className.includes("!p-0")
              ? "flex h-full flex-col"
              : getContentClassName(isExpanded)
          }
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
